import React, { useState } from "react";
import { toast } from "react-toastify";

import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { FileCopyOutlined, CheckCircle } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

// Marcadores que a gente substitui na URL do iframe. Sem eles o painel
// abre sempre no mesmo contato, pra todo mundo -- por isso o Salvar trava.
//
// Chave simples, não dupla: a spec (PDF, §7) mostra {{contactId}}, mas a
// URL que a G-Click mandou de verdade pra Impacto usa {contactId}. Vale o
// que eles mandam.
const IFRAME_PLACEHOLDERS = ["{contactId}", "{userId}"];

const stripTrailingSlash = url => (url || "").replace(/\/+$/, "");

// Os dois valores que o usuário cola no G-Click são constantes do deploy,
// não estado da empresa -- o frontend já sabe os dois, não precisa pedir
// pro backend. O sufixo /gclick é onde a ponte REST é montada (ver
// routes/index.ts): sem ele as chamadas caem no /api compartilhado, que já
// tem rotas com o mesmo nome (/contacts, /users/:param).
const LOGIN_URL = stripTrailingSlash(window.location.origin);
const API_URL = `${stripTrailingSlash(process.env.REACT_APP_BACKEND_URL)}/gclick`;

const useStyles = makeStyles(theme => ({
  step: {
    marginBottom: theme.spacing(3)
  },
  stepHead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  stepNum: {
    flex: "none",
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: 500,
    lineHeight: "20px",
    textAlign: "center"
  },
  stepHint: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.5, 0, 2, 3.625)
  },
  stepBody: {
    marginLeft: theme.spacing(3.625)
  },
  row: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  field: {
    flex: 1,
    minWidth: 240
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "0.8rem"
  },
  // Os bullets do token já salvo não são o hash nem o token -- são enfeite.
  // Sem isso dá pra selecionar e copiar os "••••" e colar no G-Click, que
  // aceita, salva, e só falha depois na primeira chamada com 401.
  masked: {
    userSelect: "none",
    pointerEvents: "none"
  },
  emptyToken: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(2)
  },
  helperText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary
  },
  tokenActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5)
  },
  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5)
  }
}));

const GclickIframe = ({ status, onStatusChange }) => {
  const classes = useStyles();

  const hasToken = status?.hasToken ?? false;

  // O modal desmonta ao fechar (Platforms/index.js só renderiza o conteúdo
  // com modalPlatform truthy), então semear do status na montagem já pega o
  // valor atual toda vez que a tela abre.
  const [iframeUrl, setIframeUrl] = useState(status?.iframeUrl ?? "");
  const [freshToken, setFreshToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);

  const trimmedUrl = iframeUrl.trim();
  const missingPlaceholders = trimmedUrl
    ? IFRAME_PLACEHOLDERS.filter(placeholder => !trimmedUrl.includes(placeholder))
    : [];

  const handleCopy = async (value, label) => {
    if (!navigator.clipboard?.writeText) {
      toast.info("Não foi possível copiar. Selecione o texto e use Ctrl+C.");
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado.`);
    } catch (err) {
      toast.info("Não foi possível copiar. Selecione o texto e use Ctrl+C.");
    }
  };

  const handleGenerate = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/gclickSettings/token");
      // Única vez que o token aparece -- o backend guarda só o sha256.
      setFreshToken(data.token);
      onStatusChange({ hasToken: true });
      toast.success("Token gerado.");
    } catch (err) {
      toastError(err);
    } finally {
      setWorking(false);
    }
  };

  const handleSave = async () => {
    if (missingPlaceholders.length > 0) {
      toast.error("Corrija a URL do iframe antes de salvar.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/gclickSettings/iframe-url", { iframeUrl: trimmedUrl });
      onStatusChange({ iframeUrl: trimmedUrl });
      toast.success("Configurações salvas com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const copyAdornment = (value, label) => (
    <InputAdornment position="end">
      <Tooltip title="Copiar">
        <IconButton size="small" onClick={() => handleCopy(value, label)}>
          <FileCopyOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  return (
    <div>
      <ConfirmationModal
        title="Gerar um novo token?"
        open={regenConfirmOpen}
        onClose={() => setRegenConfirmOpen(false)}
        onConfirm={handleGenerate}
        confirmColor="primary"
      >
        O token atual para de funcionar na hora. Você precisa colar o novo token no
        G-Click, ou a integração fica fora do ar.
      </ConfirmationModal>

      <div className={classes.step}>
        <Box className={classes.stepHead}>
          <span className={classes.stepNum}>1</span>
          <Typography variant="subtitle1">Copie estes dados para o G-Click</Typography>
        </Box>
        <Typography className={classes.stepHint}>
          No G-Click, abra o menu <strong>Configurações → Integrações &amp; API →
          GoLiveZap</strong> e cole cada valor no campo correspondente.
        </Typography>

        <div className={classes.stepBody}>
          <Box className={classes.row}>
            <TextField
              variant="outlined"
              size="small"
              label="Url do Login Go Live Zap"
              className={classes.field}
              value={LOGIN_URL}
              InputProps={{
                readOnly: true,
                endAdornment: copyAdornment(LOGIN_URL, "Url do Login")
              }}
            />
            <TextField
              variant="outlined"
              size="small"
              label="Url da API Go Live Zap"
              className={classes.field}
              value={API_URL}
              InputProps={{
                readOnly: true,
                endAdornment: copyAdornment(API_URL, "Url da API")
              }}
            />
          </Box>

          {!hasToken && (
            <div className={classes.emptyToken}>
              <Typography className={classes.helperText}>
                Nenhum token gerado. O G-Click precisa de um token para acessar esta conta.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={working}
              >
                Gerar token
              </Button>
            </div>
          )}

          {hasToken && freshToken && (
            <>
              <Alert severity="warning" style={{ marginBottom: 12 }}>
                <strong>Copie o token agora.</strong> Por segurança ele só é exibido uma
                vez. Se perder, gere outro.
              </Alert>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label="Token de acesso"
                value={freshToken}
                helperText="O token é salvo assim que você gera — não precisa clicar em Salvar."
                InputProps={{
                  readOnly: true,
                  classes: { input: classes.mono },
                  endAdornment: copyAdornment(freshToken, "Token")
                }}
              />
            </>
          )}

          {hasToken && !freshToken && (
            <>
              <Box className={classes.statusLine}>
                <Chip
                  size="small"
                  icon={<CheckCircle style={{ color: "#fff" }} />}
                  label="Token ativo"
                  style={{ backgroundColor: "#2e7d32", color: "#fff", fontWeight: 500 }}
                />
                <Typography className={classes.helperText}>
                  O G-Click já pode autenticar as chamadas para esta conta.
                </Typography>
              </Box>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label="Token de acesso"
                value="••••••••••••••••••••••••••••••••"
                helperText="O token não pode ser exibido de novo. Gere um novo se precisar colá-lo outra vez."
                InputProps={{
                  readOnly: true,
                  classes: { input: `${classes.mono} ${classes.masked}` }
                }}
                inputProps={{ tabIndex: -1, "aria-label": "Token salvo, oculto" }}
              />
            </>
          )}

          {/* Sem botão de revogar: desligar o G-Click é decisão do boss, pelo
              gclickEnabled (ver isGclickEnabled e gclickAuth). Gerar novo token
              já mata o anterior na hora, que é o que a empresa precisa daqui.
              O DELETE /gclickSettings/token continua de pé, só não tem UI. */}
          {hasToken && (
            <div className={classes.tokenActions}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => setRegenConfirmOpen(true)}
                disabled={working}
              >
                Gerar novo token
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={classes.step}>
        <Box className={classes.stepHead}>
          <span className={classes.stepNum}>2</span>
          <Typography variant="subtitle1">Cole a URL do iframe do G-Click</Typography>
        </Box>
        <Typography className={classes.stepHint}>
          Depois de salvar no G-Click, copie a URL que ele fornece e cole abaixo. Mantenha
          os marcadores <code>{"{contactId}"}</code> e <code>{"{userId}"}</code> — eles
          são preenchidos a cada atendimento.
        </Typography>
        <div className={classes.stepBody}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            label="URL do iframe"
            placeholder="https://g2.gclick.com.br/vertical-intro?contato={contactId}&usuario={userId}&token=…&sistema=16&id=…"
            value={iframeUrl}
            onChange={e => setIframeUrl(e.target.value)}
            error={missingPlaceholders.length > 0}
            helperText={
              missingPlaceholders.length > 0
                ? `Faltando ${missingPlaceholders.join(" e ")}. Sem os marcadores, o painel abre sempre no mesmo contato.`
                : "Com a URL salva, o painel do G-Click aparece ao lado da conversa no atendimento."
            }
            InputProps={{ classes: { input: classes.mono } }}
          />
        </div>
      </div>

      <div className={classes.stepBody}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || missingPlaceholders.length > 0}
        >
          Salvar configurações
        </Button>
      </div>
    </div>
  );
};

export default GclickIframe;
